/**
 * ProfitabilityTab - Product profitability ranking
 * Analyze profit margins and identify underperformers
 */
import { useState, useEffect } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, 
  RefreshCw, Settings, BarChart2, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

const RANK_BADGES = {
  1: 'bg-yellow-500 text-white',
  2: 'bg-gray-400 text-white',
  3: 'bg-amber-700 text-white'
};

export default function ProfitabilityTab({ authHeaders }) {
  const [ranking, setRanking] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [underperformers, setUnderperformers] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [activeView, setActiveView] = useState('ranking');
  
  // Cost editing
  const [showCostEditor, setShowCostEditor] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [costForm, setCostForm] = useState({
    ingredient_cost: '',
    labor_cost: '',
    packaging_cost: '',
    overhead_cost: ''
  });

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([
      fetchRanking(),
      fetchTopPerformers(),
      fetchUnderperformers(),
      fetchCategoryBreakdown()
    ]);
    setLoading(false);
  };

  const fetchRanking = async () => {
    try {
      const res = await api.get(`/admin/profitability/ranking?days=${days}&limit=50`);
      setRanking(res.data);
    } catch (err) {
      console.error('Failed to fetch ranking:', err);
    }
  };

  const fetchTopPerformers = async () => {
    try {
      const res = await api.get(`/admin/profitability/top-performers?days=${days}`);
      setTopPerformers(res.data);
    } catch (err) {
      console.error('Failed to fetch top performers:', err);
    }
  };

  const fetchUnderperformers = async () => {
    try {
      const res = await api.get(`/admin/profitability/underperformers?days=${days}`);
      setUnderperformers(res.data);
    } catch (err) {
      console.error('Failed to fetch underperformers:', err);
    }
  };

  const fetchCategoryBreakdown = async () => {
    try {
      const res = await api.get(`/admin/profitability/by-category?days=${days}`);
      setCategoryBreakdown(res.data);
    } catch (err) {
      console.error('Failed to fetch category breakdown:', err);
    }
  };

  const saveCost = async () => {
    if (!selectedItem) return;
    
    try {
      await api.post(`/admin/costs/${selectedItem.item_id}`, {
        ingredient_cost: parseFloat(costForm.ingredient_cost) || 0,
        labor_cost: parseFloat(costForm.labor_cost) || 0,
        packaging_cost: parseFloat(costForm.packaging_cost) || 0,
        overhead_cost: parseFloat(costForm.overhead_cost) || 0
      });
      
      toast.success('Cost updated');
      setShowCostEditor(false);
      fetchAllData();
    } catch (err) {
      toast.error('Failed to save cost');
    }
  };

  const bulkEstimateCosts = async () => {
    try {
      const res = await api.post('/admin/costs/bulk-estimate?default_margin=30');
      toast.success(`Estimated costs for ${res.data.items?.length || 0} items`);
      fetchAllData();
    } catch (err) {
      toast.error('Failed to estimate costs');
    }
  };

  const openCostEditor = (item) => {
    setSelectedItem(item);
    setCostForm({
      ingredient_cost: (item.cost * 0.7).toFixed(2),
      labor_cost: (item.cost * 0.2).toFixed(2),
      packaging_cost: (item.cost * 0.05).toFixed(2),
      overhead_cost: (item.cost * 0.05).toFixed(2)
    });
    setShowCostEditor(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profitability-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Profitability Analysis</h2>
            <p className="text-muted-foreground text-sm">Profit margins and performance ranking</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="px-3 py-2 rounded-full border border-input bg-background text-foreground text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={bulkEstimateCosts}
            className="rounded-full"
          >
            <Settings className="w-4 h-4 mr-2" />
            Auto-Estimate Costs
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAllData}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {ranking && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">${ranking.summary?.total_revenue?.toFixed(2)}</div>
            <div className="text-sm text-muted-foreground">Total Revenue</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              ${ranking.summary?.total_profit?.toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">Total Profit</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{ranking.summary?.average_margin?.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">Avg Margin</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{ranking.summary?.items_analyzed}</div>
            <div className="text-sm text-muted-foreground">Items Analyzed</div>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'ranking', label: 'Profit Ranking' },
          { id: 'top', label: 'Top Performers' },
          { id: 'under', label: 'Underperformers' },
          { id: 'category', label: 'By Category' }
        ].map(tab => (
          <Button
            key={tab.id}
            variant={activeView === tab.id ? 'default' : 'outline'}
            onClick={() => setActiveView(tab.id)}
            className="rounded-full whitespace-nowrap"
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Profit Ranking */}
      {activeView === 'ranking' && ranking?.rankings && (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Rank</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Item</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Price</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Cost</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Margin</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Sold</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Profit</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ranking.rankings.map((item) => (
                  <tr key={item.item_id} className="border-t border-border hover:bg-muted/30">
                    <td className="py-3 px-4">
                      {item.rank <= 3 ? (
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${RANK_BADGES[item.rank]}`}>
                          {item.rank}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{item.rank}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium text-foreground">{item.item_name}</td>
                    <td className="py-3 px-4 text-muted-foreground capitalize">{item.category}</td>
                    <td className="py-3 px-4 text-right text-foreground">${item.selling_price?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-muted-foreground">${item.cost?.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`font-medium ${
                        item.profit_margin >= 50 ? 'text-green-600 dark:text-green-400' :
                        item.profit_margin >= 30 ? 'text-foreground' :
                        'text-red-600 dark:text-red-400'
                      }`}>
                        {item.profit_margin?.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right text-foreground">{item.units_sold}</td>
                    <td className="py-3 px-4 text-right font-medium text-green-600 dark:text-green-400">
                      ${item.total_profit?.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCostEditor(item)}
                        className="text-xs"
                      >
                        Edit Cost
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Performers */}
      {activeView === 'top' && topPerformers && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-5 h-5 text-yellow-500" />
              <h3 className="font-heading text-lg text-foreground">Top by Profit</h3>
            </div>
            <div className="space-y-3">
              {topPerformers.top_by_profit?.slice(0, 5).map((item, i) => (
                <div key={`profit-${item.item_name}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-yellow-500 text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-foreground text-sm">{item.item_name}</span>
                  </div>
                  <span className="font-bold text-green-600 dark:text-green-400">
                    ${item.total_profit?.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              <h3 className="font-heading text-lg text-foreground">Top by Margin</h3>
            </div>
            <div className="space-y-3">
              {topPerformers.top_by_margin?.slice(0, 5).map((item, i) => (
                <div key={`margin-${item.item_name}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-foreground text-sm">{item.item_name}</span>
                  </div>
                  <span className="font-bold text-purple-600 dark:text-purple-400">
                    {item.profit_margin?.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 className="w-5 h-5 text-blue-500" />
              <h3 className="font-heading text-lg text-foreground">Top by Volume</h3>
            </div>
            <div className="space-y-3">
              {topPerformers.top_by_volume?.slice(0, 5).map((item, i) => (
                <div key={`volume-${item.item_name}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-blue-500 text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="text-foreground text-sm">{item.item_name}</span>
                  </div>
                  <span className="font-bold text-blue-600 dark:text-blue-400">
                    {item.units_sold} sold
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Underperformers */}
      {activeView === 'under' && underperformers && (
        <div className="space-y-6">
          {/* Low Margin */}
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-heading text-lg text-foreground">Low Margin Items (under 20%)</h3>
            </div>
            {underperformers.low_margin_items?.length > 0 ? (
              <div className="space-y-3">
                {underperformers.low_margin_items.map((item) => (
                  <div key={`low-margin-${item.item_name}`} className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
                    <div>
                      <div className="font-medium text-foreground">{item.item_name}</div>
                      <div className="text-sm text-muted-foreground">
                        Price: ${item.selling_price?.toFixed(2)} | Cost: ${item.cost?.toFixed(2)} | {item.units_sold} sold
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                        {item.profit_margin?.toFixed(1)}%
                      </div>
                      <div className="text-sm text-muted-foreground">margin</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No low margin items found</p>
            )}
          </div>

          {/* Recommendations */}
          {underperformers.recommendations?.length > 0 && (
            <div className="bg-card rounded-2xl border border-border p-6">
              <h3 className="font-heading text-lg text-foreground mb-4">Recommendations</h3>
              <div className="space-y-3">
                {underperformers.recommendations.map((rec) => (
                  <div key={`rec-${rec.item}`} className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                    <div className="font-medium text-blue-700 dark:text-blue-300">{rec.item}</div>
                    <div className="text-sm text-blue-600 dark:text-blue-400">{rec.suggestion}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Breakdown */}
      {activeView === 'category' && categoryBreakdown?.categories && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">Profitability by Category</h3>
          <div className="space-y-4">
            {categoryBreakdown.categories.map((cat) => (
              <div key={`cat-${cat.category}`} className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-foreground capitalize">{cat.category}</div>
                  <div className="text-right">
                    <span className="text-green-600 dark:text-green-400 font-bold">
                      ${cat.total_profit?.toFixed(2)}
                    </span>
                    <span className="text-muted-foreground text-sm ml-2">
                      ({cat.avg_margin?.toFixed(1)}% avg)
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span>{cat.item_count} items</span>
                  <span>{cat.units_sold} sold</span>
                  <span>${cat.total_revenue?.toFixed(2)} revenue</span>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full"
                    style={{ width: `${Math.min(100, cat.avg_margin)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost Editor Modal */}
      {showCostEditor && selectedItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl border border-border p-6 max-w-md w-full">
            <h3 className="font-heading text-lg text-foreground mb-4">
              Edit Cost: {selectedItem.item_name}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Ingredient Cost</label>
                <Input
                  type="number"
                  step="0.01"
                  value={costForm.ingredient_cost}
                  onChange={(e) => setCostForm({...costForm, ingredient_cost: e.target.value})}
                  className="bg-background"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Labor Cost</label>
                <Input
                  type="number"
                  step="0.01"
                  value={costForm.labor_cost}
                  onChange={(e) => setCostForm({...costForm, labor_cost: e.target.value})}
                  className="bg-background"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Packaging Cost</label>
                <Input
                  type="number"
                  step="0.01"
                  value={costForm.packaging_cost}
                  onChange={(e) => setCostForm({...costForm, packaging_cost: e.target.value})}
                  className="bg-background"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Overhead Cost</label>
                <Input
                  type="number"
                  step="0.01"
                  value={costForm.overhead_cost}
                  onChange={(e) => setCostForm({...costForm, overhead_cost: e.target.value})}
                  className="bg-background"
                />
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <div className="text-sm text-muted-foreground">Total Cost</div>
                <div className="text-xl font-bold text-foreground">
                  ${(
                    parseFloat(costForm.ingredient_cost || 0) +
                    parseFloat(costForm.labor_cost || 0) +
                    parseFloat(costForm.packaging_cost || 0) +
                    parseFloat(costForm.overhead_cost || 0)
                  ).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button onClick={saveCost} className="flex-1">Save Cost</Button>
              <Button variant="outline" onClick={() => setShowCostEditor(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
